import React, { useState, useLayoutEffect, useRef } from 'react';
import QAMessage from './QAMessage';
import { motion, AnimateSharedLayout } from 'framer-motion';
import usePubNubMessages from '../hooks/usePubNubMessages';
import { useChat } from '../modules/ChatPane';
import { sortMessagesByVotes } from '../helpers/pubNubFunctions';

// Messsage factory for moderated QA
// Renders messages in 'posted' and 'submitted' columns
export default function QAModeratedMessages({ channeltype, showVoting, showing }) {
  const { context } = useChat();
  const { content, isOpen, selectedTab, activeMessenger } = context;
  let room = content.roomForChannelType(channeltype);
  const [messages] = usePubNubMessages(room);
  const [activeTab, setActiveTab] = useState('submitted');

  // autoscrolling functionality
  const autoScrollRef = useRef(true);
  const autoScrollContainerRef = useRef(null);

  const onScroll = () => {
    if (autoScrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = autoScrollContainerRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 10) {
        autoScrollRef.current = true;
      } else {
        autoScrollRef.current = false;
      }
    }
  };

  useLayoutEffect(() => {
    if (autoScrollRef.current) {
      // this is a known usecase for setTImeout - the dom has painted (useLayoutEffect) but hasn't
      // fully rendered so by using a setTimeout we are giving it microsectonds to render while also
      // adding the checking of scrollHeight and clientHeight to the microtask queue so they can be accurate
      setTimeout(() => {
        autoScrollContainerRef.current.scrollTop =
          autoScrollContainerRef.current?.scrollHeight - autoScrollContainerRef.current?.clientHeight;
      }, 100);
    }
  }, [messages, isOpen, selectedTab, activeTab, activeMessenger]);

  let messageBody = <div className={`no_messages_text`}>No messages yet</div>;

  // algo for how we should sort these bad boys

  if (messages?.length > 0) {
    messageBody = messages.sort(sortMessagesByVotes).map((message, i) => {
      const isPosted = message.actions?.posted?.true?.length > 0;
      if (activeTab == 'posted') {
        if (isPosted) {
          return (
            <QAMessage
              key={message.timetoken}
              message={message}
              channeltype={channeltype}
              showVoting={showVoting}
              room={room}
              moderatedQAFlow={true}
              containerRef={autoScrollContainerRef}
            />
          );
        } else {
          return <div key={message.timetoken} style={{ display: 'none' }}></div>;
        }
      }
      if (activeTab == 'submitted') {
        if (isPosted) {
          return <div key={message.timetoken} style={{ display: 'none' }}></div>;
        } else {
          return (
            <QAMessage
              key={message.timetoken}
              message={message}
              channeltype={channeltype}
              showVoting={showVoting}
              room={room}
              moderatedQAFlow={true}
              containerRef={autoScrollContainerRef}
            />
          );
        }
      }
    });
  }

  return (
    <motion.div
      variants={messagesVariants}
      animate={showing ? `visible` : `hidden`}
      style={{
        flexDirection: 'column',
        flexBasis: 'auto',
        flexGrow: 1,
        overflow: 'hidden',
      }}
    >
      <div className={`moderate_qa_tabs`} style={{ display: 'flex' }}>
        <AnimateSharedLayout>
          <div className={`moderate_qa_tab`} onClick={e => setActiveTab('submitted')}>
            Submitted
            {activeTab == 'submitted' && (
              <motion.div className={`qa_tab_indicator`} transition={spring} initial={false} layoutId={`qa_tabs`} />
            )}
          </div>
          <div className={`moderate_qa_tab`} onClick={e => setActiveTab('posted')}>
            Posted
            {activeTab == 'posted' && (
              <motion.div className={`qa_tab_indicator`} transition={spring} initial={false} layoutId={`qa_tabs`} />
            )}
          </div>
        </AnimateSharedLayout>
      </div>

      <div className={`messages_scroll_container`} ref={autoScrollContainerRef} onScroll={onScroll}>
        {messageBody}
      </div>
    </motion.div>
  );
}

const messagesVariants = {
  hidden: {
    opacity: 0,
    display: 'none',
  },
  visible: {
    opacity: 1,
    display: 'flex',
  },
};

const spring = {
  type: 'spring',
  stiffness: 500,
  damping: 30,
};
